import { ValueOfObject } from 'src/types/generics';
import { HasStatus, SortableRequest } from 'src/api/Interfaces';
import { HookFormMediaObject } from 'src/components/Form/S3UploadInput';

const orderType = {
  ad: 'ad',
  talkingHead: 'talking_head',
  youtubeVideo: 'yt_video',
  podcast: 'podcast',
  vlog: 'vlog',
  reel: 'reel',
  tiktok: 'tiktok',
  youtubeShorts: 'yt_shorts',
  story: 'story',
  courseVideo: 'course_video',
  tutorial: 'tutorial',
  motionGraphics: 'motion_graphics',
  other: 'other',
} as const;

type OrderType = ValueOfObject<typeof orderType> | string;

const requestMultipleOutcomesType = {
  custom: 'custom',
  editorChoice: 'editor_choice',
} as const;

type RequestMultipleOutcomesType = ValueOfObject<typeof requestMultipleOutcomesType>;

const requestPriority = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
} as const;

type RequestPriorityType = ValueOfObject<typeof requestPriority>;

const requestPriorityLabels = {
  [requestPriority.low]: 'Low',
  [requestPriority.medium]: 'Medium',
  [requestPriority.high]: 'High',
  [requestPriority.critical]: 'Critical',
};

const requestPriorityDescriptions = {
  [requestPriority.low]: 'Adds this to the bottom of your queue.',
  [requestPriority.high]: 'Moves this to the top of your queue - editor picks it up next.',
};

const requestComplexity = {
  simple: 'simple',
  moderate: 'moderate',
  complex: 'complex',
} as const;

type RequestComplexityType = ValueOfObject<typeof requestComplexity>;

const requestComplexityLabels = {
  [requestComplexity.simple]: 'Simple',
  [requestComplexity.moderate]: 'Moderate',
  [requestComplexity.complex]: 'Complex',
};

const requestComplexityDescriptions = {
  [requestComplexity.simple]: 'Cuts, captions, music, color correction.',
  [requestComplexity.moderate]: 'Adds animated captions, B-roll, transitions, sound design.',
  [requestComplexity.complex]:
    'Adds motion graphics, rotoscoping, advanced sound design, color grading.',
};

const requestStatus = {
  draft: 'draft',
  uploading: 'uploading',
  queued: 'queued',
  currentlyEditing: 'currently_editing',
  delivered: 'delivered',
  complete: 'complete',
} as const;

type RequestStatus = ValueOfObject<typeof requestStatus>;

const requestListType = {
  list: 'list',
  kanban: 'kanban',
} as const;

type RequestListType = ValueOfObject<typeof requestListType>;

export const requestAspectRatio = {
  widescreen: '16x9',
  verticalWidescreen: '9x16',
  standardMonitor: '4x3', // deprecated
  classicFilm: '3x2', // deprecated
  cinemaScope: '21x9', // deprecated
  portrait: '4x5',
  square: '1x1',
  videoQuote: '1x1 Video Quote',
};

export type RequestAspectRatio = ValueOfObject<typeof requestAspectRatio>;

export const aspectRatioOptions = [
  {
    label: requestAspectRatio.verticalWidescreen,
    value: requestAspectRatio.verticalWidescreen,
    description: '(TikTok, Reels, Stories, Shorts)',
  },
  {
    label: requestAspectRatio.widescreen,
    value: requestAspectRatio.widescreen,
    description: '(Youtube / Web)',
  },
  {
    label: requestAspectRatio.portrait,
    value: requestAspectRatio.portrait,
    description: '(Facebook & Instagram Feed)',
  },
];

interface RequestResolutionsObject {
  main_size?: string;
  resize?: string[];
}

interface RequestAssetUrl {
  id: string;
  url: string;
  comment?: string;
  file_name?: string;
  mime_type?: string;
  file_size?: number;
}

interface RequestExtraObject {
  subtitles?: string;
  end_card?: boolean;
  examples?: string;
}

type TotalLengthRequest = {
  text?: string | number | null;
  value?: number;
  unit?: string;
};

type TotalLengthResponse = {
  value?: number;
  unit?: string;
};

interface MultipleOutcomesResponse {
  outcome_type: RequestMultipleOutcomesType;
  outcomes: OutcomeResponse[];
}

interface OutcomeResponse extends HasStatus<RequestStatus> {
  id: string;
  title: string;
  type?: OrderType;
  description: string;
  is_default?: boolean;
  total_length?: TotalLengthResponse;
  created_at: string;
  aspect_ratio?: string;
  resize?: string[];
  requested_attention_at?: string | null;
  attention_text?: string;
  requested_reedit_at?: string | null;
  priority?: RequestPriorityType;
  complexity?: RequestComplexityType;
  examples?: string;
  video_template?: {
    id: string;
    title: string;
    aspect_ratio: string;
  };
}

interface SortRequest extends SortableRequest {
  request_id: string;
}

interface MultipleOutcomesRequest {
  outcome_type: RequestMultipleOutcomesType;
  outcomes: OutcomeRequest[];
}

interface OutcomeRequest {
  // Optional — echo the existing outcome's id back so the be-core update
  // path can match the entry to an existing row and update in place
  // instead of delete-and-recreate (issue 2439). Absent for freshly
  // added outcome cards in the form.
  id?: string;
  title: string;
  type?: OrderType;
  description: string;
  is_default?: boolean;
  total_length?: TotalLengthRequest;
  video_template_id?: string;
  aspect_ratio?: string;
  resize?: string[];
  priority?: RequestPriorityType | null;
  complexity?: RequestComplexityType | null;
  examples?: string;
}

type RequestCounts = {
  total: string[];
  new: string[];
  unread_messages: string[];
};

type RequestsCountsResponse = {
  [key in RequestStatus]: RequestCounts;
} & {
  // Optional: backend's counts endpoint may or may not return archived
  // counts. The All-tab dropdown's Archived badge falls back to 0 when
  // missing. Tracked under #2386 follow-up.
  archived?: RequestCounts;
};

interface RequestFormObject {
  status: RequestStatus;
  type?: string;
  title: string;
  video_template_id?: string;
  priority: RequestPriorityType;
  complexity?: RequestComplexityType;
  media: {
    footage: HookFormMediaObject[];
  };
  supporting_materials: {
    music_preference?: string;
  };
  assets: RequestAssetUrl[];
  extra: RequestExtraObject;
  description?: {
    video_request_description?: string;
  };
  total_length: TotalLengthRequest;
  with_multiple_outcomes: boolean;
  multiple_outcomes: MultipleOutcomesRequest;
  brand_id?: string;
  resolutions?: RequestResolutionsObject;
  is_draft: boolean;
}

export type {
  RequestStatus,
  RequestListType,
  OrderType,
  TotalLengthRequest,
  TotalLengthResponse,
  RequestResolutionsObject,
  RequestAssetUrl,
  SortRequest,
  RequestMultipleOutcomesType,
  RequestPriorityType,
  RequestComplexityType,
  MultipleOutcomesResponse,
  OutcomeResponse,
  MultipleOutcomesRequest,
  OutcomeRequest,
  RequestsCountsResponse,
  RequestCounts,
  RequestExtraObject,
  RequestFormObject,
};
export {
  orderType,
  requestMultipleOutcomesType,
  requestPriority,
  requestPriorityLabels,
  requestPriorityDescriptions,
  requestComplexity,
  requestComplexityLabels,
  requestComplexityDescriptions,
  requestStatus,
  requestListType,
};
